import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { AuthShell } from '@/components/branding/auth-shell';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

type Phase = 'idle' | 'submitting' | 'sent';

function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setPhase('submitting');
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (resetErr) {
      setError(resetErr.message);
      setPhase('idle');
      return;
    }
    setPhase('sent');
  };

  return (
    <AuthShell>
      <Card className="w-full max-w-sm bg-surface/95 border-border/[0.16] shadow-2xl backdrop-blur-md">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Reset password</CardTitle>
          <CardDescription>
            {phase === 'sent'
              ? "We've sent a reset link to that email if it's registered. Check your inbox."
              : "Enter the email you sign in with — we'll send you a reset link."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {phase === 'sent' ? (
            <div className="space-y-4">
              <p className="text-sm text-text-dim">
                The link expires in an hour. If it doesn't arrive in a few minutes, check spam or try again.
              </p>
              <a
                href="/login"
                className="inline-flex w-full items-center justify-center rounded border border-border/[0.16] bg-transparent py-2 text-sm text-text-dim transition-colors hover:border-accent hover:text-text"
              >
                Back to sign in
              </a>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                void onSubmit(e);
              }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={phase === 'submitting'}
                />
              </div>
              {error ? <p className="text-danger text-sm">{error}</p> : null}
              <Button
                type="submit"
                disabled={phase === 'submitting' || !email}
                className="w-full"
              >
                {phase === 'submitting' ? 'Sending…' : 'Send reset link'}
              </Button>
              <div className="text-center text-sm">
                <a
                  href="/login"
                  className="text-text-dim hover:text-accent transition-colors"
                >
                  Back to sign in
                </a>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
